import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  RotateCcw,
  Home,
  Plus,
  X,
  Monitor,
  ChevronDown
} from 'lucide-react';

const ElectronNavigationToolbar = ({ user, isOnPublicPage }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);
  const [tabs, setTabs] = useState([]);
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [showTabMenu, setShowTabMenu] = useState(false);

  useEffect(() => {
    // Check if running in Electron
    if (window.electronAPI) {
      // Get initial tabs state
      updateTabsState();

      // Listen for tab events
      window.electronAPI.onTabStateUpdated((event, data) => {
        setCanGoBack(data.canGoBack);
        setCanGoForward(data.canGoForward);
        setTabs(data.tabs);
        setCurrentTabIndex(data.currentTabIndex);
      });

      window.electronAPI.onTabCreated((event, tab) => {
        updateTabsState();
      });

      window.electronAPI.onTabClosed((event, data) => {
        setTabs(data.tabs);
        setCurrentTabIndex(data.currentTabIndex);
      });

      window.electronAPI.onNavigateTo((event, url) => {
        navigate(url);
      });

      window.electronAPI.onShortcutTriggered((event, shortcut) => {
        handleShortcut(shortcut);
      });

      // Listen for location changes to update history
      const unlisten = () => {
        // Update tab history when route changes
        updateTabHistory(location.pathname);
      };

      return unlisten;
    }
  }, [location.pathname, navigate]);

  const updateTabsState = async () => {
    if (window.electronAPI) {
      try {
        const state = await window.electronAPI.getTabsState();
        setCanGoBack(state.canGoBack);
        setCanGoForward(state.canGoForward);
        setTabs(state.tabs);
        setCurrentTabIndex(state.currentTabIndex);
      } catch (error) {
        console.error('Failed to get tabs state:', error);
      }
    }
  };

  const updateTabHistory = (url) => {
    if (window.electronAPI && tabs[currentTabIndex]) {
      // This would be handled by the main process
      // For now, we'll just update the local state
    }
  };

  const handleBack = async () => {
    if (window.electronAPI) {
      await window.electronAPI.navigateBack();
    } else {
      navigate(-1);
    }
  };

  const handleForward = async () => {
    if (window.electronAPI) {
      await window.electronAPI.navigateForward();
    } else {
      navigate(1);
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleHome = () => {
    navigate('/');
  };

  const handleNewTab = async () => {
    if (window.electronAPI) {
      // Create new tab with current page URL (like real browser)
      const currentUrl = location.pathname;
      await window.electronAPI.createNewTab(currentUrl);
    }
  };

  const handleCloseTab = async (tabId) => {
    if (window.electronAPI) {
      await window.electronAPI.closeTab(tabId);
    }
  };

  const handleSwitchTab = async (tabId) => {
    if (window.electronAPI) {
      await window.electronAPI.switchTab(tabId);
    }
  };

  const handleShortcut = (shortcut) => {
    const routes = {
      sales: '/sales',
      purchases: '/purchases',
      payments: '/payments',
      reports: '/reports',
      items: '/items',
      expenses: '/expenses'
    };

    if (routes[shortcut]) {
      navigate(routes[shortcut]);
    }
  };

  // Only show toolbar in Electron environment
  if (!window.electronAPI) {
    return null;
  }

  return (
    <div className="bg-gray-800 text-white flex items-center justify-between px-4 py-2 border-b border-gray-700">
      {/* Navigation Controls */}
      <div className="flex items-center space-x-2">
        <button
          onClick={handleBack}
          disabled={!canGoBack}
          className={`p-2 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed`}
          title="Back (Ctrl+Left)"
        >
          <ArrowLeft size={16} />
        </button>

        <button
          onClick={handleForward}
          disabled={!canGoForward}
          className={`p-2 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed`}
          title="Forward (Ctrl+Right)"
        >
          <ArrowRight size={16} />
        </button>

        <button
          onClick={handleRefresh}
          className="p-2 rounded hover:bg-gray-700"
          title="Refresh (Ctrl+R)"
        >
          <RotateCcw size={16} />
        </button>

        <button
          onClick={handleHome}
          className="p-2 rounded hover:bg-gray-700"
          title="Home"
        >
          <Home size={16} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center space-x-1 flex-1 mx-4">
        {tabs.map((tab, index) => (
          <div
            key={tab.id}
            className={`flex items-center px-3 py-2 rounded-t cursor-pointer border-t-2 ${
              index === currentTabIndex
                ? 'bg-gray-700 border-acctoo-500 text-white'
                : 'bg-gray-600 border-transparent text-gray-300 hover:bg-gray-500'
            }`}
            onClick={() => handleSwitchTab(tab.id)}
          >
            <span className="text-sm truncate max-w-32">{tab.title}</span>
            {tabs.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseTab(tab.id);
                }}
                className="ml-2 p-1 rounded hover:bg-gray-800"
              >
                <X size={12} />
              </button>
            )}
          </div>
        ))}

        <button
          onClick={handleNewTab}
          className="p-2 rounded hover:bg-gray-700 ml-2"
          title="New Tab (Ctrl+T)"
        >
          <Plus size={16} />
        </button>
      </div>

      {/* Shortcuts Info */}
      <div className="flex items-center space-x-4 text-xs text-gray-400">
        <div className="relative">
          <button
            onClick={() => setShowTabMenu(!showTabMenu)}
            className="flex items-center space-x-1 hover:text-white"
          >
            <Monitor size={14} />
            <span>Shortcuts</span>
            <ChevronDown size={12} />
          </button>

          {showTabMenu && (
            <div className="absolute right-0 top-full mt-2 bg-gray-700 rounded shadow-lg p-3 min-w-48 z-50">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><kbd className="bg-gray-600 px-1 rounded">Ctrl+S</kbd> Sales</div>
                <div><kbd className="bg-gray-600 px-1 rounded">Ctrl+P</kbd> Purchases</div>
                <div><kbd className="bg-gray-600 px-1 rounded">Ctrl+Y</kbd> Payments</div>
                <div><kbd className="bg-gray-600 px-1 rounded">Ctrl+R</kbd> Reports</div>
                <div><kbd className="bg-gray-600 px-1 rounded">Ctrl+I</kbd> Items</div>
                <div><kbd className="bg-gray-600 px-1 rounded">Ctrl+E</kbd> Expenses</div>
                <div><kbd className="bg-gray-600 px-1 rounded">Ctrl+T</kbd> New Tab</div>
                <div><kbd className="bg-gray-600 px-1 rounded">Ctrl+W</kbd> Close Tab</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ElectronNavigationToolbar;

